import React, { useEffect, useRef, useState } from "react";
import { RefreshCw, Loader2, Wand2, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useReadingLevel } from "../context/ReadingLevelContext";
import { adaptCopy, regenerateCopy } from "../lib/copyAdaptApi";
import { formatError } from "../lib/api";

const PRESET_TWEAKS = [
  "Punchier",
  "Warmer, encouraging",
  "No analogies",
  "Shorter",
  "One sentence only",
  "More concrete, less abstract",
];

/**
 * AdaptedText — drop-in wrapper that renders `originalText` in the reader's
 * chosen level, with a discreet "Regenerate with feedback" affordance.
 *
 * Usage:
 *   <AdaptedText
 *     source="workbook_module" sourceId="module-01" field="description"
 *     originalText={mod.description}
 *     as="p" className="text-sm text-muted-foreground"
 *   />
 *
 * Behaviour:
 *   • level === 'insider' → renders `originalText` as-is, no LLM call.
 *   • level !== 'insider' → fires `adaptCopy` for a single field.
 *   • Small pill in the top-right corner appears on hover, opening a
 *     popover with preset "tweak" chips + freeform feedback. Submit
 *     overwrites the cache (via `/copy/regenerate`) so the next visitor
 *     sees this version too.
 *   • Errors degrade gracefully — the original text stays visible.
 */
export default function AdaptedText({
  source,
  sourceId,
  field,
  originalText,
  as: Tag = "p",
  className = "",
  showControls = true,
  onAdapted,
  ...rest
}) {
  const { level } = useReadingLevel();
  const [text, setText] = useState(originalText);
  const [loading, setLoading] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);
  const abortId = useRef(0);

  // Fetch adaptation on level or source change. Only if level != insider.
  useEffect(() => {
    setText(originalText);
    if (!originalText || level === "insider") return;
    const myId = ++abortId.current;
    setLoading(true);
    adaptCopy({
      source, sourceId, level,
      fields: { [field]: originalText },
    })
      .then((adapted) => {
        if (myId !== abortId.current) return;
        const next = (adapted && adapted[field]) || originalText;
        setText(next);
        onAdapted && onAdapted(next);
      })
      .catch(() => { if (myId === abortId.current) setText(originalText); })
      .finally(() => { if (myId === abortId.current) setLoading(false); });
    return () => { /* no cleanup needed — we key by abortId */ };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, source, sourceId, field, originalText]);

  const submit = async (nudge) => {
    const patch = (nudge || feedback || "").trim();
    if (!patch && !nudge) return;
    setBusy(true);
    try {
      const res = await regenerateCopy({
        source, sourceId, field, level,
        originalText, feedback: patch,
      });
      setText(res.adapted || originalText);
      onAdapted && onAdapted(res.adapted);
      setPopoverOpen(false);
      setFeedback("");
      toast.success("Regenerated");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const canShowControls = showControls && level !== "insider" && !!originalText;

  return (
    <span className="relative group inline-block align-baseline max-w-full" data-testid={`adapted-${field}`}>
      <Tag className={className} {...rest}>{text}</Tag>
      {loading && (
        <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-shell-accent align-middle">
          <Loader2 className="w-3 h-3 animate-spin" /> adapting
        </span>
      )}

      {canShowControls && (
        <span className="absolute -right-2 -top-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
          <button
            type="button"
            onClick={() => setPopoverOpen((v) => !v)}
            title="Regenerate with feedback"
            className="w-6 h-6 rounded-full border border-shell-border bg-white shadow-sm inline-flex items-center justify-center hover:border-shell-accent hover:text-shell-accent transition"
            data-testid={`adapt-regen-${field}`}
          >
            <RefreshCw className="w-3 h-3" />
          </button>
        </span>
      )}

      {popoverOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setPopoverOpen(false)} />
          <div
            className="absolute right-0 top-6 z-50 w-72 rounded-xl border border-shell-border bg-white shadow-2xl overflow-hidden"
            data-testid={`adapt-popover-${field}`}
          >
            <div className="px-4 py-3 border-b border-shell-border/60 bg-muted/40 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-shell-accent font-semibold inline-flex items-center gap-1">
                  <Wand2 className="w-3 h-3" /> Regenerate
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  Nudge the tone. Overwrites the cached version.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPopoverOpen(false)}
                className="p-1 rounded hover:bg-muted"
                aria-label="Close"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="px-4 py-3">
              <div className="flex flex-wrap gap-1.5 mb-3">
                {PRESET_TWEAKS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => submit(t)}
                    disabled={busy}
                    className="text-[11px] px-2 py-1 rounded-full border border-shell-border/70 hover:border-shell-accent hover:text-shell-accent disabled:opacity-40"
                    data-testid={`adapt-preset-${t.split(",")[0].toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <textarea
                rows={2}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Your own guidance… e.g. drop the acronyms"
                className="w-full text-xs rounded-md border border-border bg-white px-2 py-1.5 outline-none focus:border-shell-accent resize-y"
                data-testid={`adapt-feedback-${field}`}
              />
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => submit()}
                  disabled={busy || !feedback.trim()}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-shell-accent text-white font-semibold hover:opacity-90 disabled:opacity-40"
                  data-testid={`adapt-submit-${field}`}
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  Regenerate
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </span>
  );
}
